<template>
    <DsModal
        is-open
        size="fullscreen"
        show-close
        sticky-desktop-header
        class="migration-preview-modal"
        :title="$t('funnel.convert.email.preview.modal.title')"
        @close="handleClose(false)"
    >
        <template #default>
            <div class="migration-preview-container">
                <div class="migration-preview-content">
                    <DsSpinner v-if="isDryRunInProgress" />

                    <DsInlineAlert
                        v-else-if="errorDuringDryRun"
                        leading-icon
                        type="critical"
                        class="migration-alert"
                    >
                        <div class="migration-error-alert">
                            <div>
                                <p>
                                    {{ $t('funnel.convert.email.preview.audit.error.text1') }}
                                </p>

                                <p>
                                    {{ $t('funnel.convert.email.preview.audit.error.text2') }}
                                </p>
                            </div>

                            <DsFilledButton
                                gray
                                dense
                                class="retry-init-button"
                                @click="init"
                            >
                                {{ $t('global.tryAgain') }}
                            </DsFilledButton>
                        </div>
                    </DsInlineAlert>

                    <div v-else class="migration-main-content">
                        <h2 class="migration-title">
                            {{ $t('funnel.convert.email.preview.modal.heading') }}
                        </h2>

                        <section class="preview-section">
                            <p>
                                {{ $t('funnel.convert.email.preview.modal.about.text1') }}
                            </p>

                            <DsOutlineButton
                                :loading="isGeneratingPreview"
                                :disabled="isConverting"
                                class="preview-button"
                                data-qa="migration-preview-button"
                                @click="generatePreview"
                            >
                                {{ $t('funnel.convert.email.preview.modal.previewButtonText') }}
                            </DsOutlineButton>
                        </section>

                        <section class="convert-section">
                            <p>
                                {{ $t('funnel.convert.email.preview.modal.about.text2') }}
                            </p>

                            <DsInlineAlert
                                v-if="naturalLanguageAuditList.length > 0"
                                leading-icon
                                type="warning"
                                class="migration-alert"
                            >
                                <p> {{ $t('funnel.convert.email.preview.audit.warning.title') }} </p>

                                <ul>
                                    <li v-for="(item, index) in naturalLanguageAuditList" :key="index">
                                        {{ item }}
                                    </li>
                                </ul>

                                <p> {{ $t('funnel.convert.email.preview.audit.warning.eta') }}</p>
                            </DsInlineAlert>

                            <DsInlineAlert
                                v-else
                                leading-icon
                                type="good"
                                class="migration-alert"
                            >
                                <p>
                                    {{ $t('funnel.convert.email.preview.audit.allGood.text') }}
                                </p>
                            </DsInlineAlert>

                            <div>
                                <span>
                                    {{ $t('funnel.convert.email.preview.modal.about.text3.textBeforeLink') }}
                                </span>
                                <DsLink
                                    as="a"
                                    :href="$options.LEARN_MORE_URL"
                                    external
                                >
                                    {{ $t('funnel.convert.email.preview.modal.about.text3.linkText') }}
                                </DsLink>
                                <span>
                                    {{ $t('funnel.convert.email.preview.modal.about.text3.textAfterLink') }}
                                </span>
                            </div>
                        </section>

                        <section class="actions-section">
                            <DsButton
                                data-qa="migration-confirm-button"
                                :loading="isConverting"
                                @click="convert"
                            >
                                {{ $t('funnel.convert.email.preview.modal.convert.button.text') }}
                            </DsButton>

                            <DsTextButton
                                :disabled="isConverting"
                                @click="handleClose(false)"
                            >
                                {{ $t('funnel.convert.email.preview.audit.finishLater') }}
                            </DsTextButton>
                        </section>
                    </div>

                    <ContentPreviewModal
                        v-if="isPreviewModalOpen"
                        class="html-preview-modal"
                        :preview-html="unlayerHtml"
                        @close="isPreviewModalOpen = false"
                    />

                    <div v-show="false" id="unlayer-editor-to-generate-preview" />
                </div>
            </div>
        </template>
    </DsModal>
</template>

<script>
import {
    migrateToUnlayer,
    getBardJSON,
    getUnlayerMigrationPreview,
    getEmailTemplate,
    getUnlayerEditorConfigForPreview,
} from '@/components/funnel/konva-campaign-editor/elements/thens/communications/unlayerEmail/unlayer.email.api';
import { utils } from '@/components/funnel/konva-campaign-editor/utils/legacy-utils';
import { ConvertBardToUnlayer, ConvertBardHtmlToUnlayerJson } from '@infusionsoft/legacy-email-template-converter';
import { loadScript } from './unlayer.util';
import { createUnlayerEditorOptionsForPreview as getEditorOptions } from './unlayerEmail.util';
import ContentPreviewModal from './ContentPreviewModal';
import { getUsers, getUserSignatures } from '@/api/campaigns/campaignUnlayerEmail';
import {
    TEMPLATE_MIGRATION_AUDIT_TYPES,
    TEMPLATE_MIGRATION_LEARN_MORE_URL as LEARN_MORE_URL,
} from '@/components/broadcast/constants/emailBroadcast.constants';

export default {
    LEARN_MORE_URL,

    name: 'BardToUnlayerMigrationPreview',

    components: {
        ContentPreviewModal,
    },

    props: {
        marketingEmailId: {
            type: Number,
            required: true,
        },

        funnelId: {
            type: Number,
            required: true,
        },

        cellName: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            isDryRunInProgress: true,
            errorDuringDryRun: false,
            isConverting: false,
            staticMigratedUnlayerDesign: null,
            unlayerDesign: null,
            unlayerHtml: null,
            auditList: [],
            isPreviewModalOpen: false,
            isGeneratingPreview: false,
            editor: null,
        };
    },

    created() {
        this.init();
    },

    computed: {
        naturalLanguageAuditList() {
            const list = [];
            const auditItemCount = {};

            this.auditList.forEach(({ id }) => {
                if (typeof auditItemCount[id] !== 'number') {
                    auditItemCount[id] = 0;
                }

                auditItemCount[id] += 1;
            });

            Object.keys(auditItemCount).forEach((id) => {
                const count = auditItemCount[id];
                let i18nLabel;

                switch (id) {
                case TEMPLATE_MIGRATION_AUDIT_TYPES.TAGS_MORE_THAN_TEN:
                    i18nLabel = 'funnel.convert.email.preview.audit.tagsMoreThanTen';
                    break;
                case TEMPLATE_MIGRATION_AUDIT_TYPES.NO_TAGS_ALLOWED:
                    i18nLabel = 'funnel.convert.email.preview.audit.noTagsAllowed';
                    break;
                case TEMPLATE_MIGRATION_AUDIT_TYPES.BLOCK_ERROR:
                default:
                    i18nLabel = 'funnel.convert.email.preview.audit.blockError';
                    break;
                }

                list.push(this.$tc(
                    i18nLabel,
                    count,
                    { count: count.toLocaleString() },
                ));
            });

            return list;
        },
    },

    methods: {
        handleClose(converted, marketingEmailId, linkRelationshipList) {
            const detail = {
                converted,
                marketingEmailId,
                linkRelationshipList,
            };

            this.$emit('close', detail);

            window.parent.dispatchEvent(new CustomEvent('closeBardToUnlayerMigrationPreviewModal', { detail }));
        },

        async init() {
            const { marketingEmailId, funnelId, cellName } = this;

            this.isDryRunInProgress = true;
            this.errorDuringDryRun = false;

            try {
                let unlayerDesign;

                try {
                    const bardJSON = await getBardJSON(marketingEmailId);

                    unlayerDesign = ConvertBardToUnlayer(bardJSON);
                } catch (e) {
                    if (e.response?.status !== 404) {
                        throw new Error(e);
                    }

                    const { htmlBody } = await getEmailTemplate(marketingEmailId);

                    if (htmlBody === null) {
                        this.handleClose(true, marketingEmailId, []);

                        return;
                    }

                    unlayerDesign = ConvertBardHtmlToUnlayerJson(htmlBody);
                }

                const data = await getUnlayerMigrationPreview({
                    name: cellName,
                    design: unlayerDesign,
                    funnelId,
                });

                await this.initUnlayerEditor();

                this.auditList = data.errors;
                this.unlayerDesign = data.designJson;
                this.staticMigratedUnlayerDesign = unlayerDesign;

                this.errorDuringDryRun = false;
            } catch (e) {
                this.errorDuringDryRun = true;
            }

            this.isDryRunInProgress = false;
        },

        async initUnlayerEditor() {
            const [users, userSignatures, builderConfig] = await Promise.all([
                getUsers(),
                getUserSignatures(),
                getUnlayerEditorConfigForPreview(),
                loadScript(),
            ]);

            const { customToolsUri, unlayerProjectId } = builderConfig;

            const editor = window.unlayer.createEditor({
                ...getEditorOptions({
                    users,
                    userSignatures,
                    customToolsUri,
                }),
                id: 'unlayer-editor-to-generate-preview',
                projectId: parseInt(unlayerProjectId, 10),
                displayMode: 'email',
            });

            this.editor = editor;
        },

        async convert() {
            this.isConverting = true;

            try {
                const data = await migrateToUnlayer({
                    marketingEmailId: this.marketingEmailId,
                    name: this.cellName,
                    design: this.staticMigratedUnlayerDesign,
                    funnelId: this.funnelId,
                });

                const id = Number(data.emailId.toString().replace(/\D/g, ''));

                if (id === 0) {
                    throw new Error('An error occured while migrating to unlayer!');
                }

                const updatedMarketingEmailId = utils.ensureLongEndsWithL(id.toString());

                this.handleClose(true, updatedMarketingEmailId, data.linkRelationshipList);
            } catch (e) {
                this.isConverting = false;
                this.$error({ message: this.$t('funnel.convert.email.preview.convert.error') });
            }
        },

        generatePreview() {
            if (this.unlayerHtml !== null) {
                this.isPreviewModalOpen = true;

                return;
            }

            this.isGeneratingPreview = true;

            this.editor.loadDesign(JSON.parse(this.unlayerDesign));
            this.editor.addEventListener('editor:ready', () => {
                this.editor.exportHtml(({ html }) => {
                    this.unlayerHtml = html;
                    this.isGeneratingPreview = false;
                    this.isPreviewModalOpen = true;
                });
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.migration-preview-modal {
    --modal-padding: 0;
    --modal-background-color: #{$color-gray-050};
}

$width: #{px-to-rem(575)};

p, li, span {
    font-size: $font-size-sm;
}

p {
    margin: 0;
}

.migration-header-title {
    font-size: $font-size-lg;
}

.migration-title {
    font-weight: $font-weight-bold;
    margin-bottom: $spacing-200;
}

.preview-button {
    margin-top: $spacing-100;
}

.migration-preview-container,
.migration-preview-content,
.preview-section {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
}

.migration-preview-container {
    margin: 0 $spacing-200;
}

.migration-preview-content {
    padding-top: $spacing-500;
    max-width: $width;
}

.migration-main-content {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
}

.migration-info {
    margin: $spacing-100 0;
    max-width: px-to-rem(490);

    p {
        margin: 0;
    }
}

.preview-section {
    margin: 0 16%;
}

.convert-section {
    margin: $spacing-300 0;
    display: flex;
    flex-direction: column;
    gap: $spacing-150;
}

.actions-section {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: $spacing-150;
}

.migration-alert {
    width: 100%;
}

.migration-error-alert {
    display: flex;
    align-items: center;
}

.retry-init-button {
    flex-shrink: 0;
    margin-left: $spacing-800;
}

.migration-dry-run-container {
    margin-top: $spacing-100;
}

ul {
    list-style: unset;
    margin: $spacing-150 $spacing-200;
}
</style>
